import { useEffect, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { Button } from '@/components/ui/Button';
import { ErrorDialog } from '@/components/ui/ErrorDialog';
import { useAuth } from '@/lib/auth';

import {
  adminResolveAliases,
  fetchOrgPackages,
  fetchPackages,
  invalidateCache,
  queryPackage,
  type AdminResolveAliasesResponse,
} from '../api';
import { useOrg } from '../OrgProvider';
import { QueryResultCard, type QueryHistoryItem } from '../components/QueryResultCard';
import { navigateTo } from '../router';
import type { LcRouteParams, PackageSummary } from '../types';
import {
  aliasRegistryPrefixClasses,
  buildIndexTreeRoute,
  buildTreeRoute,
  displayPackageName,
  formatAskElapsed,
  registryLabel,
  relativeTime,
  singleLanguageBadgeClasses,
  singleLanguageDotClass,
} from '../utils';
import { useLiveElapsed } from '../hooks';

interface PackageDetailPageProps {
  params: LcRouteParams;
  isAdmin?: boolean;
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function PackageDetailPage({ params, isAdmin }: PackageDetailPageProps) {
  const { user, getToken, deploymentMode } = useAuth();
  const { activeOrg } = useOrg();
  const [pkgMeta, setPkgMeta] = useState<PackageSummary | null>(null);
  const [metaLoadFailed, setMetaLoadFailed] = useState(false);
  const [metaReloadKey, setMetaReloadKey] = useState(0);
  const [versions, setVersions] = useState<
    { version: string; displayVersion: string; indexedAt: string }[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [question, setQuestion] = useState('');
  const [history, setHistory] = useState<QueryHistoryItem[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const askElapsedMs = useLiveElapsed(isSubmitting);
  const [adminToken, setAdminToken] = useState<string | null>(null);
  const [isResolvingAliases, setIsResolvingAliases] = useState(false);
  const [resolveResult, setResolveResult] = useState<AdminResolveAliasesResponse | null>(null);
  const [aliasesExpanded, setAliasesExpanded] = useState(false);

  useEffect(() => {
    if (!user) {
      setAdminToken(null);
      return;
    }
    void getToken().then(setAdminToken);
  }, [user, getToken]);

  const canResolveAliases =
    isAdmin &&
    !!pkgMeta?.id &&
    (pkgMeta?.registry === 'github' || pkgMeta?.registry === 'gitlab') &&
    !!pkgMeta?.source_url;

  const handleResolveAliases = async () => {
    if (!pkgMeta?.id || !adminToken) return;
    const aliasCount = pkgMeta.aliases?.length ?? 0;
    const confirmMsg =
      aliasCount > 0
        ? `Re-resolve aliases for ${pkgMeta.name}@${pkgMeta.version}?\n\nThis will REPLACE the ${aliasCount} existing alias${aliasCount === 1 ? '' : 'es'} with freshly verified matches.`
        : `Resolve aliases for ${pkgMeta.name}@${pkgMeta.version}?\n\nNo existing aliases — this will add any verified matches.`;
    if (!window.confirm(confirmMsg)) return;
    setIsResolvingAliases(true);
    setResolveResult(null);
    try {
      const result = await adminResolveAliases(pkgMeta.id, { useLlmFallback: true }, adminToken);
      setResolveResult(result);
      invalidateCache('packages');
      const packages = await fetchPackages();
      const fresh = packages.find(
        (p) =>
          p.registry === params.registry && p.name === params.name && p.version === params.version
      );
      if (fresh) setPkgMeta((prev) => (prev ? { ...prev, aliases: fresh.aliases ?? null } : prev));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to resolve aliases.');
    } finally {
      setIsResolvingAliases(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setPkgMeta(null);
    setMetaLoadFailed(false);

    invalidateCache('packages');

    const buildMeta = (packages: PackageSummary[]) => {
      const packageGroup = packages
        .filter((p) => p.registry === params.registry && p.name === params.name)
        .sort(
          (left, right) =>
            new Date(right.indexed_at).getTime() - new Date(left.indexed_at).getTime()
        );
      const match = packageGroup.find(
        (p) =>
          p.registry === params.registry && p.name === params.name && p.version === params.version
      );
      const fallbackMeta = packageGroup[0] ?? null;
      const sortedVersions = packageGroup
        .map((p) => ({
          version: p.version,
          displayVersion: p.display_version ?? p.version,
          indexedAt: p.indexed_at,
        }))
        .sort((a, b) => {
          const normalA = (a.displayVersion || a.version).replace(/^v(?=\d)/i, '');
          const normalB = (b.displayVersion || b.version).replace(/^v(?=\d)/i, '');
          const aParts = normalA.split('.').map(Number);
          const bParts = normalB.split('.').map(Number);
          for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
            const diff = (bParts[i] ?? 0) - (aParts[i] ?? 0);
            if (diff !== 0) return diff;
          }
          return 0;
        });
      return { match, fallbackMeta, sortedVersions };
    };

    void (async () => {
      try {
        const packages = await fetchPackages();
        let { match, fallbackMeta, sortedVersions } = buildMeta(packages);

        // Fallback: if not found in public packages, try org-private packages
        if (!fallbackMeta && activeOrg) {
          const token = await getToken();
          if (token) {
            const orgPackages = await fetchOrgPackages(activeOrg.id, token).catch(() => []);
            ({ match, fallbackMeta, sortedVersions } = buildMeta(orgPackages));
          }
        }

        if (!isMounted) return;
        if (isMounted) setVersions(sortedVersions);
        const resolvedMeta = match
          ? {
              ...fallbackMeta,
              ...match,
              aliases: match.aliases?.length ? match.aliases : fallbackMeta?.aliases,
              languages: match.languages?.length ? match.languages : fallbackMeta?.languages,
              source_url: match.source_url ?? fallbackMeta?.source_url,
            }
          : fallbackMeta;
        setPkgMeta(resolvedMeta ?? null);
      } catch {
        // Non-fatal — querying still works without the metadata.
        if (isMounted) setMetaLoadFailed(true);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [params.registry, params.name, params.version, activeOrg, getToken, metaReloadKey]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const result = await queryPackage({
        question: question.trim(),
        registry: params.registry,
        name: params.name,
        version: params.version,
      });
      setHistory((prev) => [{ question: question.trim(), result }, ...prev]);
      setQuestion('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to query package.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const languages = pkgMeta?.languages ?? [];

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
      <div className="flex-1 min-w-0 space-y-6 order-2 lg:order-1">
        {/* Header */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold text-ink">
                {displayPackageName(params.registry, params.name)}
              </h2>
              {languages.map((lang) => (
                <span
                  key={lang}
                  className={`inline-flex items-center gap-1.5 border px-2 py-0.5 text-2xs font-medium ${singleLanguageBadgeClasses(lang)}`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${singleLanguageDotClass(lang)}`} />
                  {lang}
                </span>
              ))}
            </div>
            {(() => {
              const aliases = pkgMeta?.aliases ?? [];
              if (aliases.length === 0) return null;
              const repoLeaf = (pkgMeta?.name ?? '').split('/').pop()?.toLowerCase() ?? '';
              const score = (a: { name: string }): number => {
                const n = a.name.toLowerCase();
                if (n === repoLeaf) return 0;
                if (repoLeaf && n === `apache-${repoLeaf}`) return 1;
                if (repoLeaf && (n.startsWith(`${repoLeaf}-`) || n.endsWith(`-${repoLeaf}`)))
                  return 2;
                return 3;
              };
              const sorted = [...aliases].sort((a, b) => {
                const d = score(a) - score(b);
                if (d !== 0) return d;
                const lenDiff = a.name.length - b.name.length;
                if (lenDiff !== 0) return lenDiff;
                return a.name.localeCompare(b.name);
              });
              const LIMIT = 6;
              const shown = aliasesExpanded ? sorted : sorted.slice(0, LIMIT);
              const hidden = sorted.length - shown.length;
              return (
                <div className="flex flex-wrap items-center gap-1.5">
                  {shown.map((alias) => (
                    <span
                      key={`${alias.registry}:${alias.name}`}
                      className="inline-flex items-center overflow-hidden rounded border border-rule text-3xs font-medium"
                      title={`${alias.registry}:${alias.name}`}
                    >
                      <span
                        className={`px-1.5 py-0.5 ${aliasRegistryPrefixClasses(alias.registry)}`}
                      >
                        {alias.registry}
                      </span>
                      <span className="border-l border-rule bg-panel px-1.5 py-0.5 text-link">
                        {alias.name}
                      </span>
                    </span>
                  ))}
                  {hidden > 0 && (
                    <button
                      type="button"
                      onClick={() => setAliasesExpanded(true)}
                      className="rounded border border-dashed border-rule px-1.5 py-0.5 text-3xs font-medium text-dim transition-colors hover:border-accent/40 hover:text-accent"
                    >
                      +{hidden} more
                    </button>
                  )}
                  {aliasesExpanded && sorted.length > LIMIT && (
                    <button
                      type="button"
                      onClick={() => setAliasesExpanded(false)}
                      className="rounded border border-dashed border-rule px-1.5 py-0.5 text-3xs font-medium text-dim transition-colors hover:border-accent/40 hover:text-accent"
                    >
                      Show less
                    </button>
                  )}
                </div>
              );
            })()}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              {pkgMeta?.source_url && (
                <a
                  href={pkgMeta.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-accent hover:text-accent/90"
                >
                  {pkgMeta.source_url}
                  <svg
                    aria-hidden="true"
                    className="h-3 w-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </a>
              )}
              {pkgMeta?.docs_url && (
                <a
                  href={pkgMeta.docs_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-link hover:text-accent"
                >
                  <svg
                    aria-hidden="true"
                    className="h-3 w-3 shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                  Docs
                </a>
              )}
              {pkgMeta?.released_at && (
                <span
                  className="inline-flex items-center gap-1 text-xs text-dim"
                  title={new Date(pkgMeta.released_at).toLocaleString()}
                >
                  <svg
                    aria-hidden="true"
                    className="h-3 w-3 shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6.75 3v2.25M17.25 3v2.25M3.75 7.5h16.5M4.5 5.25h15a.75.75 0 01.75.75v13.5a.75.75 0 01-.75.75h-15a.75.75 0 01-.75-.75V6a.75.75 0 01.75-.75z"
                    />
                  </svg>
                  Released{' '}
                  {new Date(pkgMeta.released_at).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </span>
              )}
            </div>
          </div>
          {isAdmin && (
            <div className="flex flex-wrap items-center gap-2">
              {canResolveAliases && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => void handleResolveAliases()}
                  disabled={isResolvingAliases || !adminToken}
                  title="Re-run the GitHub→registry alias resolver. Replaces existing aliases for this package."
                >
                  {isResolvingAliases ? 'Resolving…' : 'Resolve aliases'}
                </Button>
              )}
              {deploymentMode === 'saas' && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigateTo(buildIndexTreeRoute(params))}
                >
                  Browse index
                </Button>
              )}
            </div>
          )}
        </div>

        {resolveResult && (
          <div className="lc-surface border border-good/30 p-4 text-xs">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-semibold text-good">
                  Aliases resolved — removed {resolveResult.removed}, wrote {resolveResult.written}
                </p>
                <p className="mt-0.5 text-dim">
                  {resolveResult.registry}:{resolveResult.name}@{resolveResult.version}
                </p>
                <p className="mt-0.5 font-mono text-3xs text-dim">
                  manifests: {resolveResult.stats.manifestsFound} · parsed:{' '}
                  {resolveResult.stats.candidatesParsed} · deduped:{' '}
                  {resolveResult.stats.candidatesDeduped} · verified:{' '}
                  {resolveResult.verified.length +
                    resolveResult.skipped.filter((s) => s.status === 'verified').length}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setResolveResult(null)}
                className="text-dim hover:text-ink"
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
            {resolveResult.verified.length > 0 && (
              <ul className="mt-3 space-y-1">
                {resolveResult.verified.map((v) => (
                  <li
                    key={`${v.registry}:${v.name}:${v.manifestPath}`}
                    className="flex items-center gap-2 font-mono text-2xs"
                  >
                    <span className="rounded bg-good/15 px-1.5 py-0.5 text-good">{v.registry}</span>
                    <span className="text-link">{v.name}</span>
                    {v.version && <span className="text-dim">@{v.version}</span>}
                    <span className="ml-auto text-3xs text-dim">{v.verificationMethod}</span>
                  </li>
                ))}
              </ul>
            )}
            {resolveResult.skipped.length > 0 && (
              <details className="mt-3">
                <summary className="cursor-pointer text-2xs text-dim">
                  Skipped ({resolveResult.skipped.length})
                </summary>
                <ul className="mt-1 space-y-1">
                  {resolveResult.skipped.map((s, i) => (
                    <li
                      key={`${s.manifestPath}:${i}`}
                      className="flex items-center gap-2 font-mono text-2xs"
                    >
                      <span className="rounded bg-bg px-1.5 py-0.5 text-link">{s.status}</span>
                      <span className="text-dim">
                        {s.registry}:{s.name}
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        {/* Query */}
        <div className="space-y-3">
          <form
            onSubmit={handleSubmit}
            className="lc-surface overflow-hidden border border-rule p-4"
          >
            <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-dim">
              <span className="font-semibold text-link">Ask this verified snapshot</span>
              <span aria-hidden="true">·</span>
              <span>{registryLabel(params.registry)}</span>
              <span aria-hidden="true">·</span>
              <code className="font-mono">
                {params.name}@{pkgMeta?.display_version ?? params.version}
              </code>
              {pkgMeta?.indexed_at ? (
                <>
                  <span aria-hidden="true">·</span>
                  <span title={new Date(pkgMeta.indexed_at).toLocaleString()}>
                    indexed {relativeTime(pkgMeta.indexed_at)}
                  </span>
                </>
              ) : null}
            </div>
            <p className="mb-3 text-xs leading-relaxed text-dim">
              Ask one focused API or behavior question. The answer is retrieved only from this
              registry, version, and dated index.
            </p>
            <div className="flex gap-2">
              <input
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder={`How does ${params.name}@${pkgMeta?.display_version ?? params.version}…`}
                className="lc-input flex-1"
                disabled={isSubmitting}
              />
              <Button type="submit" size="sm" disabled={isSubmitting || !question.trim()}>
                {isSubmitting ? (
                  <span className="flex items-center gap-1.5">
                    <svg
                      aria-hidden="true"
                      className="h-3.5 w-3.5 animate-spin"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Asking…
                    {askElapsedMs != null && (
                      <span className="font-mono text-2xs opacity-80">
                        {formatAskElapsed(askElapsedMs)}
                      </span>
                    )}
                  </span>
                ) : (
                  'Ask'
                )}
              </Button>
            </div>
          </form>

          {history.map((item, i) => (
            <QueryResultCard key={`${item.question}-${i}`} item={item} />
          ))}
        </div>

        {/* Package overview / summary */}
        {isLoading ? (
          <div className="lc-surface p-5">
            <div className="h-4 w-1/3 animate-pulse rounded bg-bg" />
            <div className="mt-3 space-y-2">
              <div className="h-3 w-full animate-pulse rounded bg-bg" />
              <div className="h-3 w-5/6 animate-pulse rounded bg-bg" />
              <div className="h-3 w-4/6 animate-pulse rounded bg-bg" />
            </div>
          </div>
        ) : (pkgMeta?.overview ?? pkgMeta?.summary) ? (
          <div className="lc-surface p-5">
            <p className="mb-2 text-2xs font-semibold uppercase tracking-wider text-dim">About</p>
            <div className="lc-answer">
              <Markdown remarkPlugins={[remarkGfm]}>
                {pkgMeta?.overview ?? pkgMeta?.summary ?? ''}
              </Markdown>
            </div>
          </div>
        ) : metaLoadFailed ? (
          <div className="lc-surface p-5 text-sm" role="alert">
            <p className="font-medium text-ink">Couldn't load package details.</p>
            <p className="mt-1 text-dim">
              Querying still works — only the overview and version list are unavailable.{' '}
              <button
                type="button"
                onClick={() => setMetaReloadKey((k) => k + 1)}
                className="font-medium text-accent underline underline-offset-2 hover:text-accent/80"
              >
                Retry
              </button>
            </p>
          </div>
        ) : null}

        {error && (
          <ErrorDialog title="Query Failed" message={error} onClose={() => setError(null)} />
        )}
      </div>

      <aside className="w-full order-1 lg:order-2 lg:w-44 lg:shrink-0 lg:sticky lg:top-4">
        <p className="mb-2 text-3xs font-semibold uppercase tracking-wider text-dim">Versions</p>
        <div className="flex gap-1 overflow-x-auto pb-1 lg:flex-col lg:gap-0 lg:space-y-0.5 lg:overflow-visible lg:pb-0">
          {versions.map((v) => (
            <button
              key={v.version}
              type="button"
              onClick={() => navigateTo(buildTreeRoute({ ...params, version: v.version }))}
              className={`shrink-0 px-2.5 py-1.5 text-left transition-colors lg:w-full lg:shrink ${
                v.version === params.version
                  ? 'bg-accent-soft text-accent'
                  : 'text-link hover:bg-bg'
              }`}
            >
              <span className="block font-mono text-xs">{v.displayVersion}</span>
              <span className="block text-3xs text-dim">{relativeTime(v.indexedAt)}</span>
            </button>
          ))}
        </div>
      </aside>
    </div>
  );
}
